/**
 * As reuniões de uma implantação, em tabela — a agenda vista de cima.
 *
 * **A coluna da ata diz o que este acesso enxerga, e não o que existe.** `tem_ata` chega
 * recortado pelo servidor: para o cliente, uma ata em rascunho é `false`. Um "tem ata, mas você
 * não pode ler" contaria a ele que alguém escreveu algo sobre a reunião dele — e contar isso já é
 * atravessar metade da fronteira.
 *
 * **`evento_externo_id` é sempre nulo hoje**, e a tabela não finge o contrário: não há coluna de
 * "evento no calendário" nem botão que leve a lugar nenhum.
 */

import { Link } from "react-router";
import {
  Cabecalho,
  Celula,
  CelulaCabecalho,
  Chip,
  Corpo,
  Linha,
  Tabela,
} from "../ui";
import type { EstadoDoChip } from "../ui";
import {
  formatarDuracao,
  formatarQuando,
  rotuloDoEstadoDaReuniao,
  rotuloDoTipoDeReuniao,
  type Reuniao,
} from "../../nucleo/reuniao";

const CHIP_DO_ESTADO: Record<string, EstadoDoChip> = {
  agendada: "aguardando",
  realizada: "ok",
  cancelada: "neutro",
};

export interface PropsDaListaDeReunioes {
  reunioes: readonly Reuniao[];
  /** Para onde cada linha leva. Ausente deixa a tabela só de leitura — o caso do cliente. */
  caminhoDe?: (reuniao: Reuniao) => string;
}

export function ListaDeReunioes({ reunioes, caminhoDe }: PropsDaListaDeReunioes) {
  return (
    <div data-testid="lista-de-reunioes">
      <Tabela legenda="Reuniões desta implantação, com horário, duração e estado da ata.">
        <Cabecalho>
          <Linha>
            <CelulaCabecalho>Reunião</CelulaCabecalho>
            <CelulaCabecalho>Quando</CelulaCabecalho>
            <CelulaCabecalho>Estado</CelulaCabecalho>
            <CelulaCabecalho>Ata</CelulaCabecalho>
          </Linha>
        </Cabecalho>
        <Corpo>
          {reunioes.map((reuniao) => (
            <Linha
              key={reuniao.id}
              data-testid={`reuniao-${reuniao.id}`}
              /* O modo na LINHA, e não numa célula: é propriedade da reunião, não de uma coluna.
                 Ele é sempre `manual` hoje, e sai escrito para que o dia em que deixar de ser
                 apareça aqui em vez de virar uma coluna nova que ninguém lê. */
              data-modo={reuniao.modo}
            >
              <Celula className="celula-nome">
                {caminhoDe ? (
                  <Link to={caminhoDe(reuniao)} data-testid={`abrir-reuniao-${reuniao.id}`}>
                    {reuniao.assunto}
                  </Link>
                ) : (
                  reuniao.assunto
                )}
                <div className="celula-secundaria">
                  {rotuloDoTipoDeReuniao(reuniao.tipo)} · {reuniao.visivel_ao_cliente ? "cliente" : "interna"} ·{" "}
                  {reuniao.link ? (
                    <a href={reuniao.link} target="_blank" rel="noreferrer noopener">
                      link da sala
                    </a>
                  ) : (
                    "sem link"
                  )}
                </div>
              </Celula>
              <Celula className="numero-tabular celula-secundaria">
                {formatarQuando(reuniao.agendada_para) ?? "—"}
                <div>{formatarDuracao(reuniao.duracao_min)}</div>
              </Celula>
              <Celula>
                <Chip
                  estado={CHIP_DO_ESTADO[reuniao.estado] ?? "neutro"}
                  data-testid={`estado-${reuniao.id}`}
                >
                  {rotuloDoEstadoDaReuniao(reuniao.estado)}
                </Chip>
              </Celula>
              <Celula className="celula-secundaria" data-testid={`ata-${reuniao.id}`}>
                {reuniao.ata_publicada
                  ? "publicada"
                  : reuniao.tem_ata
                    ? "em rascunho"
                    : "ainda não escrita"}
              </Celula>
            </Linha>
          ))}
        </Corpo>
      </Tabela>
    </div>
  );
}
